import { SubmitHandler, useForm } from 'react-hook-form';
import axios from 'axios';
import GuestI from '../interfaces/Guest';
import isGuestChecked from '../functions/isGuestChecked';
import getGuestByGuestID from '../functions/getGuestByGuestID';
import findGuestsByStageName from '../functions/findGuestsByStageName';
import { useContext, useEffect, useRef, useState } from 'react';
import GlobalContextI from '../interfaces/GlobalContext';
import GlobalContext from '../contexts/GlobalContext';
import { useHistory, useParams } from 'react-router-dom';
import Button from '../components/Button';
import Form from '../components/Form';
import { exitPathForOrigin } from '../functions/checkInOrigin';
// 2026-05-24: import retained but commented — see disabled mount below.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import TimeClockReminderPopup from '../components/TimeClockReminderPopup';

/**
 * True when `value` looks like a complete scanner payload — i.e. the
 * wedge has emitted its trailing CR/LF terminator. Pure helper.
 *
 * IMPORTANT: do NOT .trim() before testing. Trimming strips the
 * trailing newline we are explicitly checking for.
 *
 * Used only as a SOFT signal (accelerator) — the debounce timer
 * fires regardless. We can no longer GATE on the terminator because
 * (a) Honeywell occasionally drops it under load and (b) the wedge
 * emits multiple CRs inside the AAMVA payload itself.
 */
const isCompleteScan = (value: string): boolean => {
    if (!value) return false;
    return /[\r\n]$/.test(value);
};

/**
 * Pre-parse sanity check for the AAMVA path. Returns the failure
 * reason (operator-readable short string) or null when the payload
 * looks complete enough to hand to /api/scanner/parse.
 *
 * Diagnoses the live-scanner partial-burst case: when the wedge
 * fires before the full PDF417 has streamed into the input field,
 * the buffered text misses one of the required AAMVA field codes
 * or comes in well under the 200-char floor a real driver license
 * scan produces. The pasted-by-operator path always satisfies all
 * four checks, which is why pasting works while live scans don't.
 */
const AAMVA_MIN_LEN = 150;
const validateRawAamvaPayload = (
    raw: string,
): { ok: true } | { ok: false; reason: string } => {
    const upper = (raw || '').toUpperCase();
    if (!upper.includes('ANSI'))
        return { ok: false, reason: 'Scan incomplete — please scan again' };
    if (!upper.includes('DAQ'))
        return {
            ok: false,
            reason: 'License number missing — please scan again',
        };
    if (!upper.includes('DBB'))
        return { ok: false, reason: 'DOB not readable — please scan again' };
    if (raw.length < AAMVA_MIN_LEN)
        return { ok: false, reason: 'Scan incomplete — please scan again' };
    return { ok: true };
};

/**
 * Looser detector for the partial-AAMVA case — input that "looks
 * AAMVA-ish" but failed the strict validator above. We use this to
 * route a truncated scan into the incomplete-scan error path
 * instead of letting it fall through to a stage-name lookup with
 * garbage. Catches the common partial: a scan that snipped before
 * DAQ/DBB landed.
 */
const looksAamvaAdjacent = (s: string): boolean => {
    if (!s) return false;
    if (s.startsWith('@')) return true;
    const up = s.toUpperCase();
    if (up.includes('ANSI')) return true;
    if (up.includes('AAMVA')) return true;
    // PDF417 fragments that drop the header still tend to include
    // at least one identifying AAMVA field code somewhere.
    for (const code of ['DAQ', 'DCS', 'DAC', 'DBB', 'DBA', 'DAG', 'DCT']) {
        if (up.indexOf(code) !== -1) return true;
    }
    return false;
};

type ScanForm = { scanID: string };

// ── Test AAMVA pool ──
// Pool of ~20 sample guests for the "Generate Test AAMVA Scan" button.
// One is picked at random per click so QA can exercise new-vs-existing,
// age branches, and birthday-of-the-day code paths without a real
// scanner. Each click that hits a guestID already in the DB will route
// to the existing-guest update flow; unseen ones go to the new-guest
// TOS+signature flow.
type SampleGuest = {
    firstName: string;
    middleName?: string;
    lastName: string;
    dob: string;          // YYYY-MM-DD
    sex: '1' | '2';       // AAMVA: 1=male, 2=female
    eyes: string;         // 3-char code (BLU/BRO/GRN/HAZ/BLK/GRY)
    height: string;       // e.g. '070 in' (inches) — passed verbatim
    address: string;
    city: string;
    state: string;        // 2-char
    zip: string;          // 5 or 9 digits
    license: string;
};

const SAMPLE_GUESTS: SampleGuest[] = [
    // Adult, well above 21 — baseline new-guest case.
    { firstName: 'John', middleName: 'Michael', lastName: 'Smith',
      dob: '1990-04-12', sex: '1', eyes: 'BRO', height: '070 in',
      address: '123 Main St', city: 'Lawrence', state: 'KS', zip: '66044',
      license: 'S12345678' },
    { firstName: 'Sarah', middleName: 'Anne', lastName: 'Johnson',
      dob: '1988-07-23', sex: '2', eyes: 'BLU', height: '065 in',
      address: '456 Oak Ave', city: 'Kansas City', state: 'MO', zip: '64108',
      license: 'J98765432' },
    { firstName: 'David', lastName: 'Williams',
      dob: '1985-11-04', sex: '1', eyes: 'GRN', height: '072 in',
      address: '789 Elm Dr', city: 'Topeka', state: 'KS', zip: '66603',
      license: 'W11223344' },
    { firstName: 'Emily', middleName: 'Rose', lastName: 'Brown',
      dob: '1992-02-14', sex: '2', eyes: 'HAZ', height: '063 in',
      address: '321 Pine St', city: 'Overland Park', state: 'KS', zip: '66212',
      license: 'B55667788' },

    // Just over 21 — should pass age check.
    { firstName: 'Michael', lastName: 'Davis',
      dob: '2004-08-30', sex: '1', eyes: 'BRO', height: '071 in',
      address: '654 Maple Ln', city: 'Olathe', state: 'KS', zip: '66061',
      license: 'D33445566' },
    { firstName: 'Jessica', middleName: 'Lynn', lastName: 'Miller',
      dob: '2003-06-18', sex: '2', eyes: 'BLU', height: '066 in',
      address: '987 Cedar Rd', city: 'Lenexa', state: 'KS', zip: '66215',
      license: 'M77889900' },

    // 18-20 — should hit "under 21" warning but be allowed in.
    { firstName: 'Tyler', lastName: 'Wilson',
      dob: '2006-03-09', sex: '1', eyes: 'GRY', height: '069 in',
      address: '147 Birch Ct', city: 'Shawnee', state: 'KS', zip: '66203',
      license: 'W99001122' },
    { firstName: 'Ashley', middleName: 'Marie', lastName: 'Moore',
      dob: '2007-09-25', sex: '2', eyes: 'BRO', height: '064 in',
      address: '258 Walnut Way', city: 'Independence', state: 'MO', zip: '64050',
      license: 'M12340987' },

    // Under 18 — should be BLOCKED at the age check.
    { firstName: 'Jacob', lastName: 'Taylor',
      dob: '2009-12-01', sex: '1', eyes: 'BLU', height: '067 in',
      address: '369 Spruce Pl', city: 'Lee Summit', state: 'MO', zip: '64063',
      license: 'T55432198' },
    { firstName: 'Madison', middleName: 'Grace', lastName: 'Anderson',
      dob: '2010-05-17', sex: '2', eyes: 'HAZ', height: '062 in',
      address: '741 Aspen Cir', city: 'Liberty', state: 'MO', zip: '64068',
      license: 'A77665544' },

    // Older adults — wide age spread.
    { firstName: 'Robert', middleName: 'James', lastName: 'Thomas',
      dob: '1972-10-08', sex: '1', eyes: 'GRN', height: '073 in',
      address: '852 Hickory Hl', city: 'Wichita', state: 'KS', zip: '67202',
      license: 'T22334411' },
    { firstName: 'Linda', lastName: 'Jackson',
      dob: '1965-01-29', sex: '2', eyes: 'BLU', height: '067 in',
      address: '963 Sycamore Trl', city: 'Springfield', state: 'MO', zip: '65802',
      license: 'J88776655' },
    { firstName: 'William', middleName: 'Patrick', lastName: 'White',
      dob: '1958-06-22', sex: '1', eyes: 'BRO', height: '068 in',
      address: '159 Magnolia Blvd', city: 'St. Louis', state: 'MO', zip: '63101',
      license: 'W66554433' },

    // Birthday TODAY — flips the "Today is your birthday!" badge.
    // We fill in MM-DD by stamping today's calendar at module-load.
    (function birthdayToday() {
        const t = new Date();
        const mm = String(t.getMonth() + 1).padStart(2, '0');
        const dd = String(t.getDate()).padStart(2, '0');
        return {
            firstName: 'Birthday',
            middleName: 'Today',
            lastName: 'Special',
            dob: `1995-${mm}-${dd}`,
            sex: '1' as const,
            eyes: 'BRO',
            height: '070 in',
            address: '111 Cake St',
            city: 'Lawrence',
            state: 'KS',
            zip: '66044',
            license: 'B19950101',
        };
    })(),
    // Birthday TOMORROW (still 21+ but about-to-be) — soak case.
    (function birthdayTomorrow() {
        const t = new Date();
        t.setDate(t.getDate() + 1);
        const mm = String(t.getMonth() + 1).padStart(2, '0');
        const dd = String(t.getDate()).padStart(2, '0');
        return {
            firstName: 'Tomorrow',
            lastName: 'Birthday',
            dob: `1994-${mm}-${dd}`,
            sex: '2' as const,
            eyes: 'GRN',
            height: '065 in',
            address: '222 Cake St',
            city: 'Lawrence',
            state: 'KS',
            zip: '66044',
            license: 'B19940101',
        };
    })(),

    // Common surnames / collision testing — duplicate detection.
    { firstName: 'James', lastName: 'Smith',
      dob: '1991-03-15', sex: '1', eyes: 'BRO', height: '071 in',
      address: '333 First Ave', city: 'Lawrence', state: 'KS', zip: '66044',
      license: 'S99887766' },
    { firstName: 'Mary', middleName: 'Elizabeth', lastName: 'Smith',
      dob: '1993-08-04', sex: '2', eyes: 'BLU', height: '064 in',
      address: '444 Second St', city: 'Lawrence', state: 'KS', zip: '66045',
      license: 'S55443322' },

    // Mixed-state set — ensures state field varies for parser tests.
    { firstName: 'Christopher', middleName: 'Lee', lastName: 'Garcia',
      dob: '1987-11-19', sex: '1', eyes: 'BRO', height: '070 in',
      address: '555 Third Pl', city: 'Tulsa', state: 'OK', zip: '74103',
      license: 'G44556677' },
    { firstName: 'Amanda', lastName: 'Martinez',
      dob: '1989-04-27', sex: '2', eyes: 'HAZ', height: '066 in',
      address: '666 Fourth Rd', city: 'Omaha', state: 'NE', zip: '68102',
      license: 'M33221199' },
    { firstName: 'Daniel', middleName: 'Scott', lastName: 'Lopez',
      dob: '1995-09-12', sex: '1', eyes: 'GRN', height: '069 in',
      address: '777 Fifth Ln', city: 'Des Moines', state: 'IA', zip: '50309',
      license: 'L77665544' },
];

const buildAamvaPayload = (g: SampleGuest): string => {
    // AAMVA PDF417 v8 (ANSI 636026 for KS-style) — our parser only
    // needs the field codes, not the byte-perfect header. The first
    // line carries DAQ (license number) and we add the standard fields
    // the server's parseAamva() consumes. Dates are YYYYMMDD.
    const dobYmd = g.dob.replace(/-/g, '');
    const issueYmd = '20240101';
    // Expiry = DOB month/day + 8 years from now, simple stable value.
    const exp = new Date();
    exp.setFullYear(exp.getFullYear() + 4);
    const expYmd = `${exp.getFullYear()}${String(exp.getMonth() + 1).padStart(2, '0')}${String(exp.getDate()).padStart(2, '0')}`;
    const zip9 = (g.zip + '0000').slice(0, 9);
    const docDisc = (g.license + '00000000000000000000').slice(0, 20);

    const lines = [
        `@ANSI 636026080102DL00410288ZA03290015DLDAQ${g.license}`,
        `DCS${g.lastName.toUpperCase()}`,
        'DDEN',
        `DAC${g.firstName.toUpperCase()}`,
        `DAD${(g.middleName || 'NMN').toUpperCase()}`,
        'DDGN',
        'DCAD',
        'DCBNONE',
        'DCDNONE',
        `DBD${issueYmd}`,
        `DBB${dobYmd}`,
        `DBA${expYmd}`,
        `DBC${g.sex}`,
        `DAU${g.height}`,
        `DAY${g.eyes}`,
        'DAZBLK',
        `DAG${g.address.toUpperCase()}`,
        `DAI${g.city.toUpperCase()}`,
        `DAJ${g.state.toUpperCase()}`,
        `DAK${zip9}`,
        `DCF${docDisc}`,
        'DCGUSA',
    ];
    return lines.join('\n');
};

// Module-scope ring buffer of recently picked sample licenses. Excluded
// from the random draw so back-to-back QA never picks the same guest
// twice in a row (which previously produced guestID dup-key crashes
// once the first one was created in the DB).
const RECENT_PICK_LIMIT = 10;
const recentPicks: string[] = [];

const pickSampleAamvaPayload = (): { payload: string; guest: SampleGuest } => {
    const eligible = SAMPLE_GUESTS.filter(
        (g) => !recentPicks.includes(g.license)
    );
    const pool = eligible.length > 0 ? eligible : SAMPLE_GUESTS;
    const guest = pool[Math.floor(Math.random() * pool.length)];
    recentPicks.push(guest.license);
    if (recentPicks.length > RECENT_PICK_LIMIT) {
        recentPicks.shift();
    }
    return { payload: buildAamvaPayload(guest), guest };
};

/**
 * Detect a passport TD3 MRZ scan: two 44-char lines starting "P<".
 * Strict — won't catch random 2-line text. The server's MrzParserService
 * runs the same heuristic; the two implementations are intentionally
 * mirrored so client routing matches server detection.
 */
const looksLikeMrz = (s: string): boolean => {
    if (!s) return false;
    const lines = s
        .replace(/\r/g, '')
        .split('\n')
        .map((l) => l.trim())
        .filter(Boolean);
    if (lines.length < 2) return false;
    const [l1, l2] = lines;
    if (l1.length !== 44 || l2.length !== 44) return false;
    if (l1[0] !== 'P') return false;
    if (!/^[A-Z0-9<]+$/.test(l1) || !/^[A-Z0-9<]+$/.test(l2)) return false;
    return true;
};

const looksLikeRawAamva = (s: string): boolean => {
    if (!s) return false;
    if (s.startsWith('@')) return true;
    const head = s.slice(0, 200).toUpperCase();
    if (head.includes('ANSI ')) return true;
    // Multi-line payload with several AAMVA field codes — Zebra pre-parsed
    // output is comma- or <-delimited and single-line, so this won't collide.
    if (!/[\r\n]/.test(s)) return false;
    let hits = 0;
    for (const code of ['DAQ', 'DCS', 'DAC', 'DBB', 'DBA', 'DAG']) {
        if (s.indexOf(code) !== -1 && ++hits >= 2) return true;
    }
    return false;
};

// Heuristic license-ID match: alnum tokens 8–15 chars, allow dashes.
// Matches things like "K03-52-9693" or "S12345678" but rejects names
// and short numerics. The first matching field in a comma split is
// the candidate.
const LICENSE_LIKE = (s: string): boolean =>
    /^[A-Z0-9][A-Z0-9-]{6,14}$/i.test(s) && /[0-9]/.test(s);

/**
 * Detection for legacy comma-delimited scanner output that isn't
 * already covered by the explicit 5/6/7/13-element parsers below.
 * If this returns true we MUST NOT fall through to the stage-name
 * lookup — even if no parsing branch matches, the input is far more
 * likely a misformatted scan than a real stage-name search.
 */
const looksLikeLegacyScannerPayload = (value: string): boolean => {
    if (!value) return false;
    if (!value.includes(',')) return false;
    const parts = value.split(',').map((x) => x.trim());
    if (parts.length < 7) return false;
    const alphabetic = (s: string) => /^[A-Z][A-Z'\- ]{0,40}$/i.test(s);
    if (!alphabetic(parts[0])) return false;
    if (!alphabetic(parts[2])) return false;
    if (!/\d/.test(value)) return false;
    if (!parts.some((p) => LICENSE_LIKE(p))) return false;
    return true;
};

/**
 * Lenient extractor for the legacy scanner payload. Returns enough
 * to drive the existing-guest lookup (firstName / lastName /
 * licenseID); other fields are populated when they match obvious
 * patterns. DOB is intentionally NOT guessed — leaving it empty
 * triggers the "DOB UNREADABLE" banner on scan-new rather than
 * displaying a wrong age.
 */
const parseLegacyScannerComma = (value: string) => {
    const parts = value.split(',').map((x) => x.trim());
    const firstName = parts[0] || '';
    const middleName = parts[1] || '';
    const lastName = parts[2] || '';
    let licenseID = '';
    let licenseExpDate = '';
    let city = '';
    let state = '';

    // First license-ID-shaped field wins. Walk later fields for
    // city/state/expiration backfill.
    for (let i = 3; i < parts.length; i++) {
        const p = parts[i];
        if (!licenseID && LICENSE_LIKE(p)) {
            licenseID = p.toUpperCase();
            continue;
        }
        if (!licenseExpDate && /^\d{1,2}-\d{1,2}-\d{2,4}$/.test(p)) {
            licenseExpDate = p;
            continue;
        }
        if (!state && /^[A-Z]{2}$/.test(p)) {
            state = p;
            continue;
        }
        if (!city && /^[A-Z][A-Z' .-]{1,40}$/i.test(p) && p.length >= 3) {
            city = p;
        }
    }

    return {
        firstName,
        middleName,
        lastName,
        licenseID,
        licenseExpDate,
        city,
        state,
        // No reliable DOB — leave empty so the new-guest TOS surfaces
        // 'DOB UNREADABLE — RESCAN ID' instead of guessing.
        dateOfBirth: '',
    };
};

const CheckInForm = () => {
    const {
        register,
        formState: { errors },
        handleSubmit,
        setValue,
        watch,
        reset,
    } = useForm<ScanForm>();
    const { setGuest } = useContext<GlobalContextI>(GlobalContext);
    const [message, setMessage] = useState('');
    // ── Stage Name capture for new All Access guests ──
    // Held while the modal is open. setGuest + navigation are gated
    // on the staff entering a non-blank stageName.
    const [pendingNewGuest, setPendingNewGuest] = useState<GuestI | null>(
        null
    );
    const [stageNameInput, setStageNameInput] = useState('');
    const stageNameInputRef = useRef<HTMLInputElement | null>(null);
    const history = useHistory();
    const { type }: { type: string } = useParams();

    // ── Auto-submit on scanner input ──
    // Subscribes to RHF's watch() so we trigger on BOTH real keystrokes
    // (keyboard-wedge scanner) AND programmatic setValue() (Test button).
    //
    // Debounce-only firing (2026-05-16). The previous gate required
    // the buffer to end in CR/LF in addition to being stable for
    // SCAN_DEBOUNCE_MS — that combined gate masked Honeywell live
    // scans that intermittently drop the trailing terminator OR
    // that emit interior CRs that prematurely fired the form's
    // Enter handler. We now:
    //   • use a <textarea> for scanID so newlines survive into the
    //     React buffer (a single-line <input> strips them)
    //   • suppress Enter via onKeyDown so a wedge's terminating
    //     return cannot submit a partial buffer
    //   • fire submit purely from idle-debounce, then validate the
    //     complete buffer against AAMVA structural rules before
    //     hitting /api/scanner/parse
    //
    // 900ms covers Honeywell's worst-case mid-scan stall under
    // load. Below 750ms we saw partial fires; above 1000ms the
    // kiosk feels sluggish to staff.
    const SCAN_DEBOUNCE_MS = 900;
    const SCAN_AUTO_SUBMIT_MIN_LEN = 30;
    const scanTimerRef = useRef<number | null>(null);
    const handleContinueRef = useRef<SubmitHandler<ScanForm> | null>(null);
    // Widened to accept either the legacy single-line <input> or the
    // current <textarea> — we switched the kiosk field to textarea in
    // 2026-05-16 to preserve multiline AAMVA newlines that a single-
    // line input would silently strip.
    const scanInputRef = useRef<
        HTMLInputElement | HTMLTextAreaElement | null
    >(null);
    // ── Scan submission lock + request ordering (2026-05-09) ──
    // inFlightRef = a single global guard that prevents duplicate
    // submissions for the same physical scan. Set true at the top of
    // handleContinue, cleared in its finally. Any code path that
    // could fire a submit (watch debounce, watch newline-trigger,
    // form Enter, programmatic) checks this before scheduling work.
    //
    // reqIdRef = monotonic counter incremented on each accepted
    // submission. handleNormalizedScan captures the id at submit
    // time and re-checks after the server response — if the id no
    // longer matches the latest, the response is stale (a newer scan
    // already started) and we ignore it. Defense-in-depth against
    // any concurrency bug that could slip past the lock.
    const inFlightRef = useRef<boolean>(false);
    const reqIdRef = useRef<number>(0);
    // lastSubmittedValueRef = the trimmed scanID that LAST won the
    // submission lock. If a watch() debounce or Enter handler tries
    // to resubmit the SAME buffered string (e.g. after a stale
    // response), handleContinue rejects it. Cleared on lock release.
    const lastSubmittedValueRef = useRef<string>('');
    // Records what fired the most recent submission attempt
    // ('debounce' | 'enter' | 'manual'). Surfaced in the debug log
    // for rejected scans so we can tell whether a wedge Enter or
    // the idle timer beat the buffer to it.
    const scanSubmitSourceRef = useRef<'debounce' | 'enter' | 'manual'>(
        'debounce',
    );
    // Per-scan timing window. firstCharAt = ms timestamp of the
    // first non-empty buffer observation since the field was last
    // empty. lastCharAt = ms timestamp of the most recent watch()
    // tick. charCount = monotonic counter of watch() ticks within
    // the window. Surfaced in the success/rejection log so we can
    // measure live scanner throughput and prove (or disprove) that
    // turbo mode is introducing inter-keystroke gaps larger than
    // SCAN_DEBOUNCE_MS. Reset whenever the buffer returns to empty.
    const scanWindowStartRef = useRef<number | null>(null);
    const scanLastCharAtRef = useRef<number | null>(null);
    const scanCharTicksRef = useRef<number>(0);
    // "Wait-for-DBB" extension flag. Set true when handleContinue
    // detects an AAMVA-adjacent buffer that has ANSI + DAQ but no DBB
    // yet — interpreted as a scanner stall mid-stream. We schedule one
    // more debounce window before surfacing the operator-facing error;
    // if the buffer fills in (more chars arrive), the watch() loop
    // re-schedules the timer normally and this stays true until the
    // window resets (buffer cleared). Prevents infinite retry.
    const dobExtendedRef = useRef<boolean>(false);
    const resetScanWindow = () => {
        scanWindowStartRef.current = null;
        scanLastCharAtRef.current = null;
        scanCharTicksRef.current = 0;
        dobExtendedRef.current = false;
    };
    const captureScanTiming = (raw: string) => {
        const start = scanWindowStartRef.current;
        const last = scanLastCharAtRef.current;
        if (!start || !last) return null;
        const durationMs = last - start;
        const chars = raw.length;
        const cps = durationMs > 0 ? Math.round((chars / durationMs) * 1000) : 0;
        return { durationMs, chars, cps, ticks: scanCharTicksRef.current };
    };
    // ── Metrics reporter ────────────────────────────────────────────
    // Fire-and-forget POST to /api/scanner/metrics/ingest. We DELIBE-
    // RATELY do not send raw scan content — only an outcome label, a
    // length scalar, and the timing block. The server's sanitize()
    // function drops any unrecognized field, so even if a future
    // regression here sends raw text the server will throw it away.
    //
    // Failure is silent: scan UX must never be blocked by metrics
    // plumbing. catch(()=>{}) is intentional.
    const postScanMetric = (
        outcome:
            | 'success'
            | 'incomplete'
            | 'parser_failure'
            | 'duplicate_blocked',
        rawLen?: number,
        timing?: { durationMs: number; chars: number; cps: number; ticks: number } | null,
    ) => {
        try {
            const body: Record<string, unknown> = {
                outcome,
                source: scanSubmitSourceRef.current,
            };
            if (typeof rawLen === 'number') body.rawLen = rawLen;
            if (timing) {
                body.durationMs = timing.durationMs;
                body.cps = timing.cps;
            }
            axios
                .post('/api/scanner/metrics/ingest', body, { timeout: 3000 })
                .catch(() => undefined);
        } catch {
            // never throw from a metrics path
        }
    };
    // Visible kiosk status + the large readable error overlay. The
    // existing `message` state powers a small landing-container
    // warning paragraph; the overlay is louder and bigger and only
    // appears for hard failures (incomplete scan, parser error).
    const [scanStatus, setScanStatus] = useState<string>(
        'Waiting for scanner...',
    );
    const [scanErrorOverlay, setScanErrorOverlay] = useState<string | null>(
        null,
    );
    const overlayTimerRef = useRef<number | null>(null);
    const showScanError = (reason: string) => {
        setScanErrorOverlay(reason);
        if (overlayTimerRef.current !== null) {
            window.clearTimeout(overlayTimerRef.current);
        }
        overlayTimerRef.current = window.setTimeout(() => {
            setScanErrorOverlay(null);
            overlayTimerRef.current = null;
        }, 5000) as unknown as number;
    };
    // Debug log emitter — single shape per rejection so we can grep
    // the kiosk console without parsing free-form text. Includes
    // scan-window timing so an operator can A/B-compare turbo on
    // vs turbo off purely from log output (cps + durationMs + ticks
    // tell you the wedge's effective throughput in either mode).
    const logRejectedScan = (raw: string, reason: string) => {
        const upper = (raw || '').toUpperCase();
        const timing = captureScanTiming(raw);
        // eslint-disable-next-line no-console
        console.warn('[SCANNER] rejected', {
            reason,
            rawLen: raw.length,
            first80: raw.slice(0, 80),
            last80: raw.slice(-80),
            containsANSI: upper.includes('ANSI'),
            containsDAQ: upper.includes('DAQ'),
            containsDBB: upper.includes('DBB'),
            newlineCount: (raw.match(/[\r\n]/g) || []).length,
            source: scanSubmitSourceRef.current,
            timing,
        });
    };
    const clearScanTimer = () => {
        if (scanTimerRef.current !== null) {
            window.clearTimeout(scanTimerRef.current);
            scanTimerRef.current = null;
        }
    };

    useEffect(() => {
        const sub = watch((value) => {
            const v = value.scanID || '';
            clearScanTimer();
            // Per-window timing capture (used by the rejected-scan
            // and successful-scan logs to surface scanner throughput
            // and gap behavior under turbo mode).
            if (v.length === 0) {
                // Empty buffer = window ended (cleared by reset()
                // after submit, or by the operator). Reset timing so
                // the NEXT char starts a fresh window.
                resetScanWindow();
            } else {
                const now = Date.now();
                if (scanWindowStartRef.current === null) {
                    scanWindowStartRef.current = now;
                }
                scanLastCharAtRef.current = now;
                scanCharTicksRef.current += 1;
            }
            // Status indicator (debug visibility, kept lean — just a
            // single status string + length).
            if (v.length === 0) {
                setScanStatus('Waiting for scanner...');
            } else if (v.length < SCAN_AUTO_SUBMIT_MIN_LEN) {
                setScanStatus(`Scan length: ${v.length} characters`);
            } else {
                setScanStatus(`Scan length: ${v.length} characters`);
            }
            if (v.length < SCAN_AUTO_SUBMIT_MIN_LEN) return;
            // Lock guard: if a previous submission is still being
            // processed, do nothing. Trailing scanner chars from the
            // same physical scan can't enqueue a duplicate request.
            if (inFlightRef.current) return;
            // Debounce-only fire. The previous double-gate (debounce
            // + trailing CR/LF) masked live scans where the wedge
            // dropped or split the terminator under load. With the
            // textarea now preserving newlines AND Enter suppressed
            // on keyDown, the buffer can no longer be prematurely
            // submitted by a stray interior CR — so a 900ms idle
            // window is sufficient to identify "the wedge finished
            // typing." Final validation happens inside handleContinue.
            scanSubmitSourceRef.current = 'debounce';
            scanTimerRef.current = window.setTimeout(() => {
                if (inFlightRef.current) return;
                const fn = handleContinueRef.current;
                if (fn) handleSubmit(fn)();
            }, SCAN_DEBOUNCE_MS) as unknown as number;
        });
        return () => {
            sub.unsubscribe();
            clearScanTimer();
        };
    }, [watch, handleSubmit]);

    // On-mount: clear any leftover input + focus the field so the next
    // scan goes straight in. Runs once per page navigation. The
    // requestAnimationFrame defers focus until after layout, which makes
    // it reliable on browsers that ignore autoFocus after history.push.
    useEffect(() => {
        reset({ scanID: '' });
        const id = window.requestAnimationFrame(() => {
            scanInputRef.current?.focus();
        });
        return () => window.cancelAnimationFrame(id);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Wrap the existing submit so we cancel any pending auto-submit
    // when the form fires (Continue-button click, or programmatic
    // auto-fire). The textarea's onKeyDown intercepts Enter before
    // the form sees it, so Enter no longer reaches this path — but
    // we still mark the source as 'manual' here because the only
    // remaining triggers are the Continue button and code-path
    // submits like the Test AAMVA button.
    const onFormSubmit = (e: React.FormEvent) => {
        clearScanTimer();
        scanSubmitSourceRef.current = 'manual';
        return handleSubmit(handleContinue)(e);
    };

    const tryStageNameLookup = async (
        input: string,
        notFoundMessage: string
    ) => {
        const matches = await findGuestsByStageName(input);

        if (matches.length === 0) {
            return setMessage(notFoundMessage);
        }

        history.push(`/guests/checkin/${type}/stage-matches`, {
            matches,
            query: input,
        });
    };

    /**
     * Unified scan handler — supports AAMVA driver license / state ID
     * and MRZ TD3 passport. Routes through /api/scanner/parse, which
     * auto-detects the format and returns a NormalizedIdentity.
     *
     * The DOB on NormalizedIdentity is ISO YYYY-MM-DD; client never
     * does locale-ambiguous date parsing on scanner output.
     */
    const handleNormalizedScan = async (raw: string) => {
        setScanStatus('Parsing scan...');
        const timing = captureScanTiming(raw);
        // eslint-disable-next-line no-console
        console.log('[SCANNER] accepted', {
            rawLen: raw.length,
            newlineCount: (raw.match(/[\r\n]/g) || []).length,
            source: scanSubmitSourceRef.current,
            timing,
        });
        // Metric reported on the SUCCESS path lives in the response-
        // handler block below, so we only count it once the parser
        // actually returned. A network failure becomes parser_failure.
        // Capture the request id at submit time. After the server
        // response, we'll check that this is STILL the latest scan —
        // if a newer scan started in between, this response is
        // stale and must not mutate UI. Defense in depth on top of
        // the in-flight lock above.
        const myReqId = reqIdRef.current;
        let parsed: any;
        try {
            const res = await axios.post('/api/scanner/parse', { raw }, { timeout: 10000 });
            parsed = res.data;
        } catch (e: any) {
            const msg =
                e?.response?.data?.message ||
                'Failed to parse scan — try again or check the scanner.';
            // Big readable overlay for the kiosk operator. Also keep
            // setMessage(...) for the existing small text path so
            // automated UI tests still observe a message string.
            logRejectedScan(raw, msg);
            postScanMetric('parser_failure', raw.length, timing);
            showScanError('Parser error — please scan again');
            setScanStatus('Waiting for scanner...');
            try {
                reset({ scanID: '' });
            } catch {}
            window.requestAnimationFrame(() => {
                scanInputRef.current?.focus();
            });
            return setMessage(msg);
        }
        // Server returned a parsed payload — record success. We
        // count anything that didn't throw as a successful scan;
        // needsManualReview is still "the parser worked", just with
        // a low-confidence DOB / age.
        postScanMetric('success', raw.length, timing);
        if (reqIdRef.current !== myReqId) {
            // eslint-disable-next-line no-console
            console.log(
                '[SCANNER] response ignored — newer scan started (myReqId=',
                myReqId,
                'current=',
                reqIdRef.current,
                ')',
            );
            return;
        }
        // eslint-disable-next-line no-console
        console.log('[SCANNER] response', {
            source: parsed?.source,
            documentType: parsed?.documentType,
            dob: parsed?.dateOfBirth,
            age: parsed?.age,
            needsManualReview: parsed?.needsManualReview,
            reviewReasons: parsed?.reviewReasons,
        });
        // ── Tripwire: ISO contract from server ──
        // Server /api/scanner/parse guarantees dateOfBirth is empty
        // OR /^\d{4}-\d{2}-\d{2}$/. Anything else is a regression.
        // Silent alarm — does not block the flow.
        if (
            parsed?.dateOfBirth &&
            !/^\d{4}-\d{2}-\d{2}$/.test(String(parsed.dateOfBirth))
        ) {
            // eslint-disable-next-line no-console
            console.error(
                'NON-ISO DOB FROM SERVER',
                String(parsed.dateOfBirth),
            );
        }


        // ── Diagnostic: surface DOB-unreadable client-side ──
        // Triggered by intermittent failures reported 2026-05-08.
        // Logs only the four fields the user requested. No network
        // call, no stored data — DevTools console only. Server-side
        // SCAN_DOB_UNREADABLE audit captures the structured row.
        if (!parsed?.dateOfBirth || parsed?.needsManualReview === true) {
            // eslint-disable-next-line no-console
            console.warn('[DOB-DIAG] DOB unreadable in scan response', {
                dateOfBirth: parsed?.dateOfBirth,
                needsManualReview: parsed?.needsManualReview,
                reviewReasons: parsed?.reviewReasons,
                source: parsed?.source,
            });
        }

        // ── Phase 3: ban gate (kiosk side) ──
        // /api/scanner/parse sets banned=true when the scanned
        // identity matches an active ban. Route to the DO NOT
        // ADMIT screen with minimal context (last 4 + first name).
        // Do NOT continue into existing-guest lookup or new-guest
        // TOS — those would create a record we don't want.
        if (parsed?.banned === true) {
            await setGuest({
                firstName: parsed.firstName || '',
                lastName: parsed.lastName || '',
                licenseID: String(parsed.documentNumber || ''),
                bannedReason: parsed.banReason || '',
                guestID: '',
                middleName: '',
                streetAddress: '',
                city: '',
                state: '',
                licenseExpDate: '',
                sex: '',
                eyes: '',
                height: '',
                weight: '',
                standing: 'GOOD',
                type,
                entryDates: [],
                dateOfBirth: parsed.dateOfBirth || '',
            } as any);
            return history.push(`/guests/checkin/${type}/banned`);
        }

        if (!parsed?.firstName || !parsed?.lastName) {
            return setMessage(
                (parsed?.reviewReasons || []).join(' ') ||
                    'Scan missing required name fields. Re-scan or enter manually.'
            );
        }
        // documentNumber is the AAMVA DAQ for licenses or the
        // passport number for MRZ. Same field powers the
        // existing-guest lookup hash either way.
        const docNumber = String(parsed.documentNumber || '');
        if (!docNumber) {
            return setMessage(
                'Scan missing document number. Re-scan or enter manually.'
            );
        }

        const guest: GuestI = {
            firstName: parsed.firstName,
            middleName: parsed.middleName || '',
            lastName: parsed.lastName,
            // Empty DOB when needsManualReview so the new-guest TOS
            // banner surfaces "DOB UNREADABLE — RESCAN ID" rather
            // than displaying a half-confident date.
            dateOfBirth: parsed.needsManualReview
                ? ''
                : parsed.dateOfBirth || '',
            streetAddress: parsed.streetAddress || '',
            city: parsed.city || '',
            state: parsed.state || '',
            licenseID: docNumber,
            licenseExpDate: parsed.expirationDate || '',
            sex: parsed.sex || '',
            eyes: parsed.eyeColor || '',
            height: parsed.height || '',
            weight: parsed.weight || '',
            standing: 'GOOD',
            type,
            entryDates: [],
            guestID: '',
            // ── Scanner audit fields (NormalizedIdentity v2.0) ──
            // rawAamvaData kept for back-compat with rows created
            // before the unified pipeline; rawScanData is canonical.
            rawAamvaData: parsed.source === 'aamva' ? (parsed.rawPayload || '') : '',
            rawScanData: parsed.rawPayload || '',
            scanSource: parsed.source || '',
            documentType: parsed.documentType || '',
            issuingStateOrCountry: parsed.issuingStateOrCountry || '',
            parserWarnings: Array.isArray(parsed.parserWarnings)
                ? parsed.parserWarnings
                : [],
            needsManualReview: !!parsed.needsManualReview,
            parsedAt: new Date().toISOString(),
            parserVersion: 'scanner-v2.0',
        };

        if (!guest.firstName || !guest.lastName || !guest.licenseID) {
            return setMessage(
                'Scan was missing required name or document fields.'
            );
        }

        // ── Phase 2 dual-read: idHash-first lookup ──
        // Server hashes the licenseID (HMAC-SHA256) and returns the
        // matching guest, or null on miss/collision. This lookup
        // matches even if the scanner emits the license with
        // different formatting (hyphens, spacing, casing) than the
        // stored row, because both ends normalize via the same
        // idhash.lib code path. The legacy guestID/oldGuestID
        // lookups below are unchanged and still run when this hits
        // null.
        let hashMatch: GuestI | null = null;
        try {
            const res = await axios.post(
                `/api/guests/${type}/find-by-license`,
                { licenseID: guest.licenseID },
                { timeout: 10000 },
            );
            // Defense-in-depth: server returns { banned: true } when
            // an active ban matches. Route immediately to DO NOT ADMIT.
            if (res.data && res.data.banned === true) {
                await setGuest({
                    ...guest,
                    bannedReason: res.data.reason || '',
                } as any);
                return history.push(`/guests/checkin/${type}/banned`);
            }
            if (res.data && res.data.guestID) hashMatch = res.data;
        } catch (e: any) {
            // Don't break the scan flow on a lookup error — the
            // server-side audit log records hits/misses/collisions,
            // and the legacy lookups below provide the fallback.
            // eslint-disable-next-line no-console
            console.warn('[idHash lookup] failed; falling back to legacy', e?.message || e);
        }
        if (hashMatch) {
            // ── Prefer fresh ISO over legacy stored DOB ──
            // 12k+ legacy DB rows have DD/MM-YYYY or empty DOB. The
            // fresh scan went through the server ISO contract; trust
            // it over the row when the row isn't strict ISO. Also
            // PATCH the row in the background so it migrates.
            const storedDob = String(hashMatch.dateOfBirth || '');
            const storedIsIso = /^\d{4}-\d{2}-\d{2}$/.test(storedDob);
            const merged: GuestI =
                !storedIsIso && guest.dateOfBirth
                    ? { ...hashMatch, dateOfBirth: guest.dateOfBirth }
                    : hashMatch;
            if (!storedIsIso && guest.dateOfBirth && hashMatch._id) {
                axios
                    .patch(
                        `/api/guests/${hashMatch._id}?type=${hashMatch.type || type}`,
                        { dateOfBirth: guest.dateOfBirth },
                    )
                    .catch(() => {
                        /* non-blocking — staff can manually save later */
                    });
            }
            await setGuest(merged);
            return history.push(
                `/guests/checkin/${type}/${hashMatch.guestID}/update`,
            );
        }

        const guestID =
            guest.firstName[0] +
            guest.lastName[0] +
            guest.licenseID.substr(guest.licenseID.length - 4);
        const oldGuestID =
            guest.firstName +
            guest.licenseID.substr(guest.licenseID.length - 4);

        const guestData = await getGuestByGuestID(guestID.toLowerCase(), type);
        const oldGuestData = await getGuestByGuestID(
            oldGuestID.toLowerCase(),
            type
        );

        if (
            guestData?.guestID === guestID.toLowerCase() ||
            oldGuestData?.guestID === oldGuestID.toLowerCase()
        ) {
            const legacyHit = (guestData || oldGuestData) as any;
            const storedDob = String(legacyHit?.dateOfBirth || '');
            const storedIsIso = /^\d{4}-\d{2}-\d{2}$/.test(storedDob);
            const merged =
                !storedIsIso && guest.dateOfBirth
                    ? { ...legacyHit, dateOfBirth: guest.dateOfBirth }
                    : legacyHit;
            if (!storedIsIso && guest.dateOfBirth && legacyHit?._id) {
                axios
                    .patch(
                        `/api/guests/${legacyHit._id}?type=${legacyHit.type || type}`,
                        { dateOfBirth: guest.dateOfBirth },
                    )
                    .catch(() => {
                        /* non-blocking */
                    });
            }
            await setGuest(merged);
            return history.push(`/guests/checkin/${type}/${guestID}/update`);
        }

        // ── New-guest split ──
        // All Access requires staff to set a stage name BEFORE the
        // TOS/signature flow. Limited has no stage name and proceeds
        // straight to scan-new.
        if (type === 'allAccess') {
            setStageNameInput('');
            setPendingNewGuest(guest);
            return;
        }

        await setGuest(guest);
        return history.push(`/guests/checkin/scan-new/${type}`);
    };

    /**
     * Confirm handler for the stage-name modal. Stamps stageName onto
     * the pending guest, hands it off to the global guest context, and
     * routes into the new-guest TOS/signature flow.
     */
    const confirmStageName = async () => {
        const trimmed = stageNameInput.trim();
        if (!trimmed || !pendingNewGuest) return;
        const next: GuestI = { ...pendingNewGuest, stageName: trimmed };
        const target = pendingNewGuest.type || type;
        setPendingNewGuest(null);
        setStageNameInput('');
        await setGuest(next);
        return history.push(`/guests/checkin/scan-new/${target}`);
    };

    /** Cancel handler — discard pending guest and refocus the scan box. */
    const cancelStageName = () => {
        setPendingNewGuest(null);
        setStageNameInput('');
        // Slight delay so the modal unmount finishes before we steal focus.
        window.setTimeout(() => {
            scanInputRef.current?.focus();
        }, 50);
    };

    const handleLegacyScanner = async (rawInput: string) => {
        const parsed = parseLegacyScannerComma(rawInput);
        if (
            !parsed.firstName ||
            !parsed.lastName ||
            !parsed.licenseID
        ) {
            return setMessage(
                'Scan was missing required name or license fields. Re-scan or enter manually.'
            );
        }

        const guest: GuestI = {
            firstName: parsed.firstName,
            middleName: parsed.middleName,
            lastName: parsed.lastName,
            dateOfBirth: parsed.dateOfBirth,
            streetAddress: '',
            city: parsed.city,
            state: parsed.state,
            licenseID: parsed.licenseID,
            licenseExpDate: parsed.licenseExpDate,
            sex: '',
            eyes: '',
            height: '',
            weight: '',
            standing: 'GOOD',
            type,
            entryDates: [],
            guestID: '',
        };

        const guestID =
            guest.firstName[0] +
            guest.lastName[0] +
            guest.licenseID.substr(guest.licenseID.length - 4);
        const oldGuestID =
            guest.firstName +
            guest.licenseID.substr(guest.licenseID.length - 4);

        const guestData = await getGuestByGuestID(
            guestID.toLowerCase(),
            type
        );
        const oldGuestData = await getGuestByGuestID(
            oldGuestID.toLowerCase(),
            type
        );

        if (
            guestData?.guestID === guestID.toLowerCase() ||
            oldGuestData?.guestID === oldGuestID.toLowerCase()
        ) {
            await setGuest(guestData || oldGuestData);
            return history.push(
                `/guests/checkin/${type}/${guestID}/update`
            );
        }

        await setGuest(guest);
        return history.push(`/guests/checkin/scan-new/${type}`);
    };

    const handleContinue: SubmitHandler<ScanForm> = async ({ scanID }) => {
        // ── Hard short-input guard ──
        // Drops scans below the auto-submit min, regardless of how
        // they got here (Enter key, debounce, newline trigger, the
        // Test Scan button). Prevents the len=1 SCAN_REJECTED that
        // a scanner's leading CR can otherwise produce.
        // NOTE: do NOT trim before checking AAMVA payloads — the
        // structural checks below want the raw newlines/whitespace
        // preserved. We only use `trimmed` for the length-based
        // short-input guard and the dedupe key.
        const raw = scanID || '';
        const trimmed = raw.trim();
        if (trimmed.length < SCAN_AUTO_SUBMIT_MIN_LEN) {
            // eslint-disable-next-line no-console
            console.log(
                '[SCANNER] submit dropped — input too short:',
                trimmed.length,
            );
            return;
        }
        // ── Incomplete-AAMVA guard (2026-05-16) ──
        // If the buffer looks AAMVA-adjacent (starts with @ / ANSI /
        // contains any DL field code) but fails the strict structural
        // checks, surface a big readable error and DROP — do NOT fall
        // through to /api/scanner/parse with a partial payload. The
        // partial would either return a soft "needsManualReview"
        // (silent UX failure) or, worse, succeed with garbage values.
        if (looksAamvaAdjacent(raw)) {
            const check = validateRawAamvaPayload(raw);
            if (!check.ok) {
                // ── Wait-for-DBB extension (2026-06-17) ──
                // Honeywell scanner intermittently stalls mid-stream
                // under load: the buffer has ANSI + DAQ but the tail
                // (where DBB lives) hasn't landed yet, so the 900ms
                // idle debounce fires too early. Instead of rejecting,
                // re-arm the debounce ONCE — if the rest of the scan
                // shows up in the next window, the watch() handler
                // will reset the timer normally and we'll re-enter
                // this path with a complete buffer. If nothing more
                // arrives, the timer fires again, the extension flag
                // is now true, and we surface the operator error
                // exactly as before.
                const upper = (raw || '').toUpperCase();
                const isDobMissing =
                    upper.includes('ANSI') &&
                    upper.includes('DAQ') &&
                    !upper.includes('DBB');
                if (isDobMissing && !dobExtendedRef.current) {
                    dobExtendedRef.current = true;
                    // eslint-disable-next-line no-console
                    console.log(
                        '[SCANNER] DBB missing — extending debounce one window',
                    );
                    scanTimerRef.current = window.setTimeout(() => {
                        if (inFlightRef.current) return;
                        const fn = handleContinueRef.current;
                        if (fn) handleSubmit(fn)();
                    }, SCAN_DEBOUNCE_MS) as unknown as number;
                    return;
                }

                logRejectedScan(raw, check.reason);
                postScanMetric('incomplete', raw.length, captureScanTiming(raw));
                // ── Server-side diagnostic (2026-06-17) ──
                // Make the existing SCAN_DOB_UNREADABLE audit actually
                // fire for the frontend-pre-validation path. PII-safe:
                // no raw content, only structured booleans/counts.
                try {
                    const reasonCode = !upper.includes('ANSI')
                        ? 'ansi_missing'
                        : !upper.includes('DAQ')
                        ? 'license_missing'
                        : !upper.includes('DBB')
                        ? 'dob_missing'
                        : 'too_short';
                    axios
                        .post(
                            '/api/scanner/diagnostics/client-reject',
                            {
                                reason: reasonCode,
                                rawLen: raw.length,
                                hasAnsi: upper.includes('ANSI'),
                                hasDaq: upper.includes('DAQ'),
                                hasDbb: upper.includes('DBB'),
                                newlineCount: (raw.match(/[\r\n]/g) || [])
                                    .length,
                                ctrlCharCount: (
                                    raw.match(
                                        // eslint-disable-next-line no-control-regex
                                        /[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g,
                                    ) || []
                                ).length,
                                source: scanSubmitSourceRef.current,
                            },
                            { timeout: 3000 },
                        )
                        .catch(() => undefined);
                } catch {
                    // Diagnostic must never block UX.
                }
                showScanError(check.reason);
                setScanStatus('Waiting for scanner...');
                // Clear the buffer so the next live scan starts clean.
                try {
                    reset({ scanID: '' });
                } catch {}
                // Refocus the scanner field so the next swipe lands
                // in the right place without operator intervention.
                window.requestAnimationFrame(() => {
                    scanInputRef.current?.focus();
                });
                return;
            }
        }
        // ── Submission lock ──
        // Exactly one in-flight scan at a time. Any duplicate
        // submission attempt (multi-fire from the same physical
        // scan, double-Enter, etc.) is dropped here.
        if (inFlightRef.current) {
            // eslint-disable-next-line no-console
            console.log('[SCANNER] submit dropped — previous still in flight');
            postScanMetric('duplicate_blocked', raw.length);
            return;
        }
        // ── Identical-buffer dedupe ──
        // If the SAME trimmed input that we just submitted shows up
        // again (a watch() debounce that fires on stale state, an
        // Enter on already-cleared input that somehow re-populates,
        // etc.), drop it. Cleared in finally so a real retry of the
        // same code is still allowed after the user re-scans.
        if (
            lastSubmittedValueRef.current &&
            lastSubmittedValueRef.current === trimmed
        ) {
            // eslint-disable-next-line no-console
            console.log(
                '[SCANNER] submit dropped — duplicate of last value',
            );
            postScanMetric('duplicate_blocked', raw.length);
            return;
        }
        inFlightRef.current = true;
        reqIdRef.current += 1;
        lastSubmittedValueRef.current = trimmed;
        // Clear the input immediately so any TRAILING scanner chars
        // (the bytes that arrive AFTER the newline that triggered
        // this submit) can't refill the field above the threshold
        // and queue another scan during the in-flight window.
        try { reset({ scanID: '' }); } catch {}
        // Watchdog: if the in-flight cycle stalls for any reason
        // (network hang, axios hung, unhandled exception path that
        // somehow misses the finally), force-release the lock at
        // +15s so the kiosk recovers and the next scan goes through.
        // Logs a console.error so we can spot it in DevTools later.
        const myWatchdogReqId = reqIdRef.current;
        const watchdogId = window.setTimeout(() => {
            if (
                inFlightRef.current &&
                reqIdRef.current === myWatchdogReqId
            ) {
                // eslint-disable-next-line no-console
                console.error(
                    '[SCANNER] watchdog forced lock release (stuck >15s)',
                );
                inFlightRef.current = false;
                lastSubmittedValueRef.current = '';
                if (scanInputRef.current) {
                    try { scanInputRef.current.focus(); } catch {}
                }
                setMessage(
                    'Scan timed out — please scan again.',
                );
            }
        }, 15000);

        try {
        setMessage('');

        const hour = new Date().getHours();
        if (hour >= 6 && hour < 11 && type === 'allAccess') {
            return setMessage(
                'Checking in has been disabled for today! Comeback tomorrow!'
            );
        }

        // Primary scanner flow — Honeywell Solaris XP and any keyboard-
        // wedge device emitting raw AAMVA PDF417 or passport MRZ TD3.
        // Both routes go through /api/scanner/parse; legacy Zebra
        // pre-parsed DL-field mode is the fallback below.
        if (looksLikeRawAamva(scanID) || looksLikeMrz(scanID)) {
            return handleNormalizedScan(scanID);
        }

        const data: string[] = scanID.includes(',')
            ? scanID.split(',')
            : (scanID.split('<').filter((el) => el !== '') as string[]);
        let guest: GuestI;
        let lengthTest: boolean = true;
        const isReadyForParse =
            data.length === 13 ||
            data.length === 5 ||
            data.length === 6 ||
            data.length === 7 ||
            scanID.length === 6;

        data.forEach((element: string) => {
            if (element.length >= 60) {
                lengthTest = false;
                return setMessage(
                    'Input from scanner is too long! Check if your device is correctly configured!'
                );
            }
        });

        if (!lengthTest) {
            return;
        }

        // Classification log — temporary, helpful while we shake out
        // edge cases in scanner output across multiple device brands.
        // eslint-disable-next-line no-console
        console.log('[CHECKIN INPUT CLASSIFICATION]', {
            type,
            rawInputLen: scanID.length,
            hasComma: scanID.includes(','),
            partsLen: data.length,
            looksLikeLegacyScannerPayload:
                looksLikeLegacyScannerPayload(scanID),
            isReadyForParse,
        });

        if (!isReadyForParse) {
            // Hard guard — never run stage-name lookup on input that
            // looks like scanner output. The 5/6/7/13 length-specific
            // parsers above handle the common cases; the legacy
            // handler covers the long-form comma split we got from
            // the new device.
            if (looksLikeLegacyScannerPayload(scanID)) {
                return handleLegacyScanner(scanID);
            }
            if (type === 'allAccess' && scanID.trim().length > 0) {
                return tryStageNameLookup(
                    scanID,
                    `No all-access guest found with stage name "${scanID}".`
                );
            }
            return;
        }

        if (scanID.length === 6) {
            const guestID = scanID;
            const guestStatus = await isGuestChecked(scanID, type);
            const guestData = await getGuestByGuestID(
                guestID.toLowerCase(),
                type
            );

            if (guestStatus === 'error') {
                return setMessage('API ERROR - CONTACT ADMIN!');
            }

            if (!guestStatus) {
                if (type === 'allAccess') {
                    return tryStageNameLookup(
                        scanID,
                        `No guest found with Loyalty Number or Stage Name "${guestID}". Please scan the ID or enter information manually.`
                    );
                }
                return setMessage(
                    `There's no guest with a Loyalty Number of ${guestID}, please scan your guest's ID or Enter the information manually!`
                );
            }

            await setGuest(guestData);

            return history.push(`/guests/checkin/${type}/${guestID}/update`);
        }

        if (data.length === 5) {
            guest = {
                city: '',
                dateOfBirth: '',
                eyes: '',
                guestID: '',
                height: '',
                licenseExpDate: '',
                middleName: '',
                sex: '',
                standing: '',
                state: '',
                streetAddress: '',
                weight: '',
                firstName: data[2],
                lastName: data[1].substring(3),
                licenseID: data[3],
            };

            const guestID =
                guest.firstName[0] +
                guest.lastName[0] +
                guest.licenseID.substr(guest.licenseID.length - 4);

            const oldGuestID =
                guest.firstName +
                guest.licenseID.substr(guest.licenseID.length - 4);

            const guestData = await getGuestByGuestID(
                guestID.toLowerCase(),
                type
            );
            const oldGuestData = await getGuestByGuestID(
                oldGuestID.toLowerCase(),
                type
            );

            if (
                guestData?.guestID === guestID.toLowerCase() ||
                oldGuestData?.guestID === oldGuestID.toLowerCase()
            ) {
                if (!guestData) {
                    await setGuest(oldGuestData);
                } else {
                    await setGuest(guestData);
                }

                return history.push(
                    `/guests/checkin/${type}/${guestID}/update`
                );
            }

            await setGuest(guest);

            return history.push(`/guests/checkin/scan-new/${type}`);
        }

        if (data.length === 6) {
            guest = {
                city: '',
                dateOfBirth: '',
                eyes: '',
                guestID: '',
                height: '',
                licenseExpDate: '',
                middleName: data[3],
                sex: '',
                standing: '',
                state: '',
                streetAddress: '',
                weight: '',
                firstName: data[2],
                lastName: data[1].substring(3),
                licenseID: data[4],
            };

            const guestID =
                guest.firstName[0] +
                guest.lastName[0] +
                guest.licenseID.substr(guest.licenseID.length - 4);

            const oldGuestID =
                guest.firstName +
                guest.licenseID.substr(guest.licenseID.length - 4);

            const guestData = await getGuestByGuestID(
                guestID.toLowerCase(),
                type
            );
            const oldGuestData = await getGuestByGuestID(
                oldGuestID.toLowerCase(),
                type
            );

            if (
                guestData?.guestID === guestID.toLowerCase() ||
                oldGuestData?.guestID === oldGuestID.toLowerCase()
            ) {
                if (!guestData) {
                    await setGuest(oldGuestData);
                } else {
                    await setGuest(guestData);
                }

                return history.push(
                    `/guests/checkin/${type}/${guestID}/update`
                );
            }

            await setGuest(guest);

            return history.push(`/guests/checkin/scan-new/${type}`);
        }

        if (data.length === 7) {
            guest = {
                city: '',
                dateOfBirth: '',
                eyes: '',
                guestID: '',
                height: '',
                licenseExpDate: '',
                middleName: data[4],
                sex: '',
                standing: '',
                state: '',
                streetAddress: '',
                weight: '',
                firstName: data[3],
                lastName: `${data[1].substring(3)} ${data[2]}`,
                licenseID: data[5],
            };

            const guestID =
                guest.firstName[0] +
                guest.lastName[0] +
                guest.licenseID.substr(guest.licenseID.length - 4);

            const oldGuestID =
                guest.firstName +
                guest.licenseID.substr(guest.licenseID.length - 4);

            const guestData = await getGuestByGuestID(
                guestID.toLowerCase(),
                type
            );
            const oldGuestData = await getGuestByGuestID(
                oldGuestID.toLowerCase(),
                type
            );

            if (
                guestData?.guestID === guestID.toLowerCase() ||
                oldGuestData?.guestID === oldGuestID.toLowerCase()
            ) {
                if (!guestData) {
                    await setGuest(oldGuestData);
                } else {
                    await setGuest(guestData);
                }

                return history.push(
                    `/guests/checkin/${type}/${guestID}/update`
                );
            }

            await setGuest(guest);

            return history.push(`/guests/checkin/scan-new/${type}`);
        }

        // Zebra 13-element scanner output for US licenses defaults to
        // MM/DD/YYYY (or MM/DD/YY for older firmware). Caller has
        // already done .replaceAll('/', '-'), so input here is one
        // of: "MM-DD-YYYY", "MM-DD-YY", or some malformed variant.
        // Output is **YYYY-MM-DD** ISO regardless — that's what every
        // downstream consumer (parseDob, MongoDB persistence, age
        // calc) expects post-DOB-hardening.
        //
        // 2-digit year disambiguation: if YY >= (currentYear+8 mod 100)
        // we treat it as 19YY (likely a DOB), otherwise 20YY. Same
        // heuristic as before — only the OUTPUT format changed.
        //
        // If we can't parse at all we log it and return the original
        // string, which parseDob will then handle (or reject as
        // unreadable) — never silently swap.
        const parseLicenseDate = (date: string) => {
            const dateSplit = date.split('-');
            if (dateSplit.length !== 3) {
                // eslint-disable-next-line no-console
                console.warn(
                    '[DOB PARSE] zebra: unexpected segment count',
                    date
                );
                return date;
            }

            const getExpInt = () => {
                const expDate = new Date().getFullYear() + 8;
                return parseInt(`${expDate}`.split('20')[1]);
            };

            // Normalize year to 4 digits (YY → 19YY/20YY).
            const yearSeg = dateSplit[2];
            const year =
                yearSeg.length === 4
                    ? yearSeg
                    : yearSeg.length === 2
                    ? parseInt(yearSeg) >= getExpInt()
                        ? `19${yearSeg}`
                        : `20${yearSeg}`
                    : '';
            if (!year) {
                // eslint-disable-next-line no-console
                console.warn(
                    '[DOB PARSE] zebra: bad year segment',
                    yearSeg
                );
                return date;
            }

            // US Zebra default: position 0 = month, position 1 = day.
            const month = dateSplit[0].padStart(2, '0');
            const day = dateSplit[1].padStart(2, '0');

            // Sanity: month must be 1..12, day 1..31. If position-0 is
            // > 12 the scanner is misconfigured — refuse to guess and
            // return the raw string so parseDob marks it unreadable
            // rather than producing the wrong date.
            const mm = parseInt(month, 10);
            const dd = parseInt(day, 10);
            if (
                !Number.isFinite(mm) ||
                !Number.isFinite(dd) ||
                mm < 1 ||
                mm > 12 ||
                dd < 1 ||
                dd > 31
            ) {
                // eslint-disable-next-line no-console
                console.warn(
                    '[DOB PARSE] zebra: out-of-range month/day',
                    { month, day, year, raw: date }
                );
                return date;
            }

            const iso = `${year}-${month}-${day}`;
            // eslint-disable-next-line no-console
            console.log(
                '[DOB PARSE] zebra:',
                date,
                '→',
                iso,
                '(US MM-DD-YYYY assumed)'
            );
            return iso;
        };

        guest = {
            firstName: data[0],
            middleName: data[1],
            lastName: data[2],
            dateOfBirth: parseLicenseDate(
                data[3].split(' ')[0].replaceAll('/', '-')
            ),
            streetAddress: data[4],
            city: data[5],
            state: data[6],
            licenseID: data[7],
            licenseExpDate: parseLicenseDate(
                data[8].split(' ')[0].replaceAll('/', '-')
            ),
            sex: data[9],
            eyes: data[10],
            height: data[11],
            weight: data[12],
            standing: 'GOOD',
            type: type,
            entryDates: [],
            guestID: '',
        };

        const guestID =
            guest.firstName[0] +
            guest.lastName[0] +
            guest.licenseID.substr(guest.licenseID.length - 4);

        const oldGuestID =
            guest.firstName +
            guest.licenseID.substr(guest.licenseID.length - 4);

        const guestData = await getGuestByGuestID(guestID.toLowerCase(), type);
        const oldGuestData = await getGuestByGuestID(
            oldGuestID.toLowerCase(),
            type
        );

        if (
            guestData?.guestID === guestID.toLowerCase() ||
            oldGuestData?.guestID === oldGuestID.toLowerCase()
        ) {
            if (!guestData) {
                await setGuest(oldGuestData);
            } else {
                await setGuest(guestData);
            }

            return history.push(`/guests/checkin/${type}/${guestID}/update`);
        }

        await setGuest(guest);

        return history.push(`/guests/checkin/scan-new/${type}`);
        } finally {
            // Always release the lock + clear watchdog. If the page
            // navigated away (handleNormalizedScan/find-by-license/
            // banned/new-guest path), the component is already
            // unmounted and the ref is gone — harmless. If we
            // returned early via setMessage, the lock clears so the
            // user can retry the same card immediately (clearing
            // lastSubmittedValueRef avoids the dedupe blocking a
            // legitimate retry).
            inFlightRef.current = false;
            lastSubmittedValueRef.current = '';
            try { window.clearTimeout(watchdogId); } catch {}
            // Refocus the scan input so the next scan lands
            // somewhere visible. Ignored if the component unmounted
            // (ref will be null on the unmounted instance).
            if (scanInputRef.current) {
                try { scanInputRef.current.focus(); } catch {}
            }
        }
    };

    // Keep the ref pointed at the latest handleContinue so the watch()
    // subscription closure always invokes the current version (avoids
    // the stale-closure trap with useEffect dep arrays).
    handleContinueRef.current = handleContinue;

    return (
        <>
            {/*
              Large readable error overlay — only renders while
              scanErrorOverlay is non-null. 32px white text on red,
              centered, auto-dismisses after 5s via showScanError().
              Sits above the rest of the layout (z-index 9999) so an
              operator standing at the kiosk can read it across the
              room.
            */}
            {scanErrorOverlay && (
                <div
                    role="alert"
                    aria-live="assertive"
                    style={{
                        position: 'fixed',
                        top: '15%',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        background: '#d40000',
                        color: '#ffffff',
                        padding: '24px 36px',
                        borderRadius: '10px',
                        fontSize: '32px',
                        fontWeight: 700,
                        textAlign: 'center',
                        maxWidth: '90vw',
                        boxShadow: '0 4px 30px rgba(0,0,0,0.45)',
                        zIndex: 9999,
                        lineHeight: 1.25,
                    }}
                >
                    {scanErrorOverlay}
                </div>
            )}
            <Form onSubmit={onFormSubmit}>
                {message !== '' && (
                    <div className="landing-container__text">
                        <p>{message}</p>
                    </div>
                )}
                <div>
                    <span className="form-span">Scanner Input</span>
                    {/*
                      Visible status line for the kiosk operator. The
                      states are explicit — "Waiting for scanner..."
                      while empty, "Scan length: N characters" once
                      bytes start arriving, "Parsing scan..." while
                      the API call is in flight. Helps an operator
                      tell at a glance whether a scan even reached
                      the field.
                    */}
                    <p
                        style={{
                            fontSize: '14px',
                            opacity: 0.7,
                            margin: '2px 0 6px 0',
                        }}
                        aria-live="polite"
                    >
                        {scanStatus}
                    </p>
                    {errors.scanID?.type === 'required' && (
                        <p className="landing-container__warning">
                            This field is required!
                        </p>
                    )}
                    {(() => {
                        const reg = register('scanID', { required: true });
                        return (
                            <textarea
                                {...reg}
                                ref={(el) => {
                                    reg.ref(el);
                                    scanInputRef.current = el;
                                }}
                                onKeyDown={(e) => {
                                    // Suppress Enter (and the wedge's
                                    // CR / LF terminators). A wedge
                                    // can fire interior CRs as part
                                    // of the AAMVA payload; without
                                    // this guard, the first one
                                    // submits the form with only the
                                    // header buffered. We catch BOTH
                                    // Enter and the explicit \r/\n
                                    // keys so any wedge profile lands
                                    // safely.
                                    if (
                                        e.key === 'Enter' ||
                                        e.key === '\n' ||
                                        e.key === '\r'
                                    ) {
                                        e.preventDefault();
                                        // Don't bubble — the wedge
                                        // sometimes fires the Enter
                                        // event AFTER the rest of
                                        // the payload has streamed
                                        // in; bubbling could still
                                        // hit a parent form submit.
                                        e.stopPropagation();
                                    }
                                }}
                                rows={2}
                                spellCheck={false}
                                autoCapitalize="off"
                                autoCorrect="off"
                                autoComplete="off"
                                placeholder={
                                    type === 'limited'
                                        ? "Scan Guest's ID"
                                        : 'Scan ID, enter Loyalty Number, or Stage Name...'
                                }
                                className="form-input"
                                style={{
                                    // Don't force monospace etc. —
                                    // staff still type loyalty
                                    // numbers in here. Just give it
                                    // enough vertical space that a
                                    // long AAMVA scan is visible
                                    // mid-stream for debugging.
                                    resize: 'none',
                                    width: '100%',
                                    fontFamily: 'inherit',
                                }}
                                autoFocus
                            />
                        );
                    })()}
                </div>
                <Button color={'green'} margin={true}>
                    Continue
                </Button>
                <Button
                    color={type}
                    margin={true}
                    onClick={() => {
                        setGuest(null);
                        return history.push(
                            `/guests/checkin/create/guest/${type}/1`
                        );
                    }}
                >
                    Manual Check-In
                </Button>
                <Button
                    color={'blue'}
                    margin={true}
                    onClick={(ev: any) => {
                        if (ev?.preventDefault) ev.preventDefault();
                        const { payload, guest } = pickSampleAamvaPayload();
                        // Surface who we picked so QA can sanity-check
                        // the random draw without inspecting the input.
                        setMessage(
                            `Test guest: ${guest.firstName} ${guest.lastName} (DOB ${guest.dob})`
                        );
                        setValue('scanID', payload, {
                            shouldDirty: true,
                            shouldTouch: true,
                            shouldValidate: true,
                        });
                        // Fire immediately — the watch() debounce would
                        // also catch this, but submitting directly makes
                        // the QA path deterministic.
                        clearScanTimer();
                        scanSubmitSourceRef.current = 'manual';
                        handleSubmit(handleContinue)();
                    }}
                >
                    Generate Test AAMVA Scan
                </Button>
                <Button
                    color={'red'}
                    margin={true}
                    onClick={() => history.push(exitPathForOrigin())}
                >
                    Go Back
                </Button>
            </Form>

            {pendingNewGuest && type === 'allAccess' && (
                <div
                    role="dialog"
                    aria-modal="true"
                    aria-label="Enter Stage Name"
                    style={{
                        position: 'fixed',
                        inset: 0,
                        background: 'rgba(0, 0, 0, 0.65)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 9000,
                    }}
                    onClick={(e) => {
                        // Click on backdrop (not the dialog itself) cancels.
                        if (e.target === e.currentTarget) cancelStageName();
                    }}
                >
                    <div
                        style={{
                            background: '#1c1c1c',
                            color: '#fff',
                            padding: '28px 32px',
                            borderRadius: 12,
                            minWidth: 360,
                            maxWidth: '90vw',
                            border: '1px solid #444',
                            boxShadow: '0 12px 40px rgba(0,0,0,0.6)',
                        }}
                    >
                        <h2 style={{ marginTop: 0, marginBottom: 8 }}>
                            Stage Name Required
                        </h2>
                        <p style={{ marginTop: 0, opacity: 0.8 }}>
                            New All Access guest:{' '}
                            <strong>
                                {pendingNewGuest.firstName}{' '}
                                {pendingNewGuest.lastName}
                            </strong>
                        </p>
                        <label
                            htmlFor="stage-name-input"
                            style={{
                                display: 'block',
                                marginBottom: 6,
                                fontWeight: 600,
                            }}
                        >
                            Enter Stage Name
                        </label>
                        <input
                            id="stage-name-input"
                            ref={stageNameInputRef}
                            type="text"
                            value={stageNameInput}
                            onChange={(e) =>
                                setStageNameInput(e.target.value)
                            }
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    confirmStageName();
                                } else if (e.key === 'Escape') {
                                    e.preventDefault();
                                    cancelStageName();
                                }
                            }}
                            placeholder="Stage name"
                            autoFocus
                            style={{
                                width: '100%',
                                padding: '10px 12px',
                                fontSize: 18,
                                borderRadius: 6,
                                border: '1px solid #666',
                                background: '#111',
                                color: '#fff',
                                marginBottom: 18,
                                boxSizing: 'border-box',
                            }}
                        />
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                gap: 10,
                            }}
                        >
                            <button
                                type="button"
                                onClick={cancelStageName}
                                style={{
                                    padding: '10px 18px',
                                    background: '#444',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: 6,
                                    cursor: 'pointer',
                                    fontSize: 15,
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={confirmStageName}
                                disabled={!stageNameInput.trim()}
                                style={{
                                    padding: '10px 18px',
                                    background: stageNameInput.trim()
                                        ? '#2e7d32'
                                        : '#2e7d3266',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: 6,
                                    cursor: stageNameInput.trim()
                                        ? 'pointer'
                                        : 'not-allowed',
                                    fontSize: 15,
                                    fontWeight: 700,
                                }}
                            >
                                Confirm
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* 2026-05-24: TimeClockReminderPopup disabled per operator request — annoying during service.
                Re-enable by removing this comment wrapper. */}
            {/* <TimeClockReminderPopup /> */}
        </>
    );
};

export default CheckInForm;
